/**
 * The template preview (PRD 10.4, "simulate the workflow as a specific
 * employee type"), as a pure function of the choices on screen.
 *
 * It runs the real engine -- the same `materialize()` and `settle()` the
 * Durable Object will run -- in the browser, with nothing stored and nobody
 * assigned. That makes it the one screen Stage 0 has: the owner can see what
 * the PRD's tables turn into before a single account exists.
 */

import {
  ACCESS_CHANGE,
  ACCESS_CHANGE_TEMPLATE,
  STARTER_TEMPLATES,
  libraryTemplate,
  materialize,
  settle,
  siteSettings,
  softWaitingOn,
  waitingOn,
  type Answer,
  type DeadlineSpec,
  type Question,
  type Template,
  testsField,
  type ProfileKey,
} from "@utuwrk/core";

export interface PreviewOptions {
  templateId: string;
  /** A template to run instead of a starter by id: the one open in the editor, saved or not. */
  template?: Template;
  /** Answers to its questions; one not given counts as "yes", so the preview shows the most cards. */
  answers?: Record<string, Answer>;
  employeeType: string;
  employmentStatus: string;
  trigger: string;
  anchorDate: string;
  hipaa: boolean;
  timeZone: string;
  now: number;
}

export interface PreviewRow {
  key: string;
  title: string;
  department: string;
  kind: "task" | "notify";
  when: string;
  dueDay: string | null;
  state: "ready" | "waiting" | "sent";
  waitsOn: string[];
  softWaitsOn: string[];
  timeCritical: boolean;
  compliance: boolean;
  compressed: boolean;
  reads: string[];
  signoffs: string[];
  /** The day it opens on, when it has one of its own. */
  opensOn: string | null;
  gate: boolean;
  /** Conditions decided while the work runs, in words: "Only if “Where they work” is Office". */
  branches: string[];
  /** An outside address told when it fires. */
  mailTo: string | null;
}

export interface Preview {
  template: Template;
  /** Which choices change anything for this template, so the screen offers only those. */
  asks: {
    employeeType: boolean;
    employmentStatus: boolean;
    trigger: boolean;
    anchorDate: boolean;
    questions: Question[];
  };
  rows: PreviewRow[];
  excluded: string[];
  notes: string[];
}

/** How the template wrote the deadline, in the words the PRD's tables use. */
export function describeDeadline(spec: DeadlineSpec): string {
  if ("minutes" in spec) return `${spec.minutes} min after it starts`;
  const sign = spec.days > 0 ? "+" : spec.days < 0 ? "−" : "";
  const amount = `${sign}${Math.abs(spec.days)}`;
  const unit = spec.unit === "calendar" ? " (calendar)" : "";
  return spec.from === "anchor" ? `Day ${amount}${unit}` : `Notice day ${amount}${unit}`;
}

export function templateById(id: string): Template {
  return libraryTemplate(id) ?? STARTER_TEMPLATES[0]!;
}

/** The trigger a template runs under: the access change is its own, the reasons for leaving the others', an onboarding none. */
export function triggerFor(template: Template, chosen: string): string {
  if (template.id === ACCESS_CHANGE_TEMPLATE.id || template.use === "access-change") return ACCESS_CHANGE;
  if (template.use === "termination") return "termination";
  return template.kind === "offboarding" ? chosen : "";
}

export function preview(options: PreviewOptions): Preview {
  const template = options.template ?? templateById(options.templateId);
  const answers = Object.fromEntries(
    (template.questions ?? []).map((question) => [question.key, options.answers?.[question.key] ?? "yes"]),
  );
  const made = materialize(template, {
    profile: {
      employeeType: options.employeeType,
      employmentStatus: options.employmentStatus,
      department: "",
      trigger: triggerFor(template, options.trigger),
      hipaa: options.hipaa,
      ...(template.questions?.length ? { answers } : {}),
    },
    settings: siteSettings([{ timeZone: options.timeZone }]),
    anchorDate: options.anchorDate,
    triggeredAt: options.now,
    assignees: {},
  });
  const { instance } = settle(made.instance, options.now);
  const title = (key: string) => instance.nodes[key]?.title ?? key;

  const rows = instance.order.map((key): PreviewRow => {
    const node = instance.nodes[key]!;
    const task = instance.tasks[key]!;
    return {
      key,
      title: node.title,
      department: node.department,
      kind: node.kind,
      when: describeDeadline(node.deadline),
      dueDay: task.dueDay,
      state:
        node.kind === "notify" && task.state === "completed"
          ? "sent"
          : task.state === "unblocked"
            ? "ready"
            : "waiting",
      waitsOn: waitingOn(instance, key).map(title),
      softWaitsOn: softWaitingOn(instance, key).map(title),
      timeCritical: node.timeCritical === true,
      compliance: node.compliance === true,
      compressed: task.compressed,
      signoffs: node.signoffs ?? [],
      opensOn: task.notBeforeDay ?? null,
      gate: node.gate === true,
      mailTo: node.mailTo ?? null,
      branches: (node.conditions ?? []).flatMap((condition) => {
        if (!("recorded" in condition)) return [];
        const label =
          instance.nodes[condition.recorded.node]?.fields?.find(
            (field) => field.key === condition.recorded.field,
          )?.label ?? condition.recorded.field;
        const values = condition.values.join(" or ");
        return [condition.op === "in" ? `Only if "${label}" is ${values}` : `Not if "${label}" is ${values}`];
      }),
      reads: (node.inputs ?? []).map(
        (link) =>
          `${instance.nodes[link.node]?.fields?.find((field) => field.key === link.field)?.label ?? link.field} (from ${title(link.node)})`,
      ),
    };
  });

  const tests = (field: ProfileKey) => testsField(template, field);
  const asks = {
    employeeType: tests("employeeType"),
    employmentStatus: tests("employmentStatus"),
    trigger: tests("trigger"),
    anchorDate: template.nodes.some((node) => node.deadline.from === "anchor"),
    questions: template.questions ?? [],
  };
  return {
    template,
    asks,
    rows,
    excluded: made.excluded.map((key) => template.nodes.find((node) => node.key === key)?.title ?? key),
    // Unassigned is the preview's normal state, so `made.unstaffed` is left out.
    notes: made.notes,
  };
}
